import * as THREE from 'three';

/**
 * SceneCleanupManager - Three.js nesnelerinin temizliğini yönetir
 *
 * Wire çizimi sırasında oluşturulan geçici nesneleri (temp line, snap marker,
 * preview symbol, etc.) düzgün bir şekilde temizler ve dispose eder.
 *
 * Kullanım:
 * ```javascript
 * SceneCleanupManager.cleanupRef(tempLineRef, scene);
 * SceneCleanupManager.cleanupWiringSession(allRefs, scene);
 * ```
 */
export class SceneCleanupManager {
  /**
   * Tek bir ref'i temizler (Line, Mesh, Group, etc.)
   * @param {Object} ref - useRef objesi (.current ile erişilen)
   * @param {THREE.Scene} scene - Three.js scene
   * @param {boolean} resetRef - Ref'i null'a set etsin mi? (varsayılan: true)
   */
  static cleanupRef(ref, scene, resetRef = true) {
    if (!ref || !ref.current) return;

    const object = ref.current;

    // ✅ Parent'tan kaldır (scene veya başka bir parent)
    try {
      if (object.parent) {
        object.parent.remove(object);
      } else if (scene) {
        scene.remove(object);
      }
    } catch (e) {
      // Fallback: scene'den direkt kaldırmayı dene
      try {
        if (scene) scene.remove(object);
      } catch (e2) {
        console.warn('Failed to remove object from scene', e2);
      }
    }

    // Geometry dispose
    try {
      if (object.geometry) {
        object.geometry.dispose();
      }
    } catch (e) {
      console.warn('Failed to dispose geometry', e);
    }

    // Material dispose (tek veya array)
    try {
      if (object.material) {
        if (Array.isArray(object.material)) {
          object.material.forEach(mat => {
            if (mat && mat.dispose) mat.dispose();
          });
        } else if (object.material.dispose) {
          object.material.dispose();
        }
      }
    } catch (e) {
      console.warn('Failed to dispose material', e);
    }

    // Group ise children'ları da temizle
    try {
      if (object.children && object.children.length > 0) {
        object.traverse(child => {
          try {
            if (child.geometry && child.geometry.dispose) child.geometry.dispose();
          } catch (e) {}
          try {
            if (child.material) {
              if (Array.isArray(child.material)) {
                child.material.forEach(mat => {
                  if (mat && mat.dispose) mat.dispose();
                });
              } else if (child.material.dispose) {
                child.material.dispose();
              }
            }
          } catch (e) {}
        });
      }
    } catch (e) {
      console.warn('Failed to cleanup children', e);
    }

    // Ref'i sıfırla
    if (resetRef) {
      ref.current = null;
    }
  }

  /**
   * Ref array'ini temizler (örn: previewMarkersRef)
   * @param {Object} ref - useRef objesi, current değeri array olmalı
   * @param {THREE.Scene} scene - Three.js scene
   * @param {boolean} clearArray - Array'i temizlesin mi? (varsayılan: true)
   */
  static cleanupRefArray(ref, scene, clearArray = true) {
    if (!ref || !ref.current || !Array.isArray(ref.current)) return;

    const array = ref.current;

    array.forEach(item => {
      if (!item) return;

      // Item kendisi bir object ise (line, mesh, etc.)
      if (item.geometry || item.material) {
        if (scene) scene.remove(item);
        if (item.geometry) item.geometry.dispose();
        if (item.material) {
          if (Array.isArray(item.material)) {
            item.material.forEach(mat => {
              if (mat) mat.dispose();
            });
          } else {
            item.material.dispose();
          }
        }
      }

      // Item bir marker group ise ({ startIdx, lines })
      if (item.lines && Array.isArray(item.lines)) {
        item.lines.forEach(line => {
          if (!line) return;
          if (scene) scene.remove(line);
          if (line.geometry) line.geometry.dispose();
          if (line.material) line.material.dispose();
        });
      }
    });

    // Array'i temizle
    if (clearArray) {
      ref.current.length = 0;
    }
  }

  /**
   * Offset line'ları temizler (tek veya çift çizgi)
   * @param {Object} offsetLineRef - useRef objesi
   * @param {THREE.Scene} scene - Three.js scene
   */
  static cleanupOffsetLines(offsetLineRef, scene) {
    if (!offsetLineRef || !offsetLineRef.current) return;

    const lines = Array.isArray(offsetLineRef.current)
      ? offsetLineRef.current
      : [offsetLineRef.current];

    lines.forEach(line => {
      if (!line) return;
      if (scene) scene.remove(line);
      if (line.geometry) line.geometry.dispose();
      if (line.material) line.material.dispose();
    });

    offsetLineRef.current = null;
  }

  /**
   * Sahnedeki orphan geçici objelerini (offset preview, snap marker, temp wire
   * preview) userData işaretleri ve isim örüntüleri ile bulup siler. Ref'lere
   * dokunmaz — yalnızca sahneden temizler. Esc/finalize akışlarının dışında da
   * (örn. iptal-yarısı kalan, branch geçişi sonrası unutulan) çağrılabilir.
   * @param {THREE.Scene} scene
   * @returns {number} silinen obje sayısı
   */
  static removeOrphanGuides(scene) {
    if (!scene) return 0;
    const orphans = [];
    scene.traverse((obj) => {
      const ud = obj.userData || {};
      // 1) Magenta/cyan offset preview rehberleri
      if (ud.isOffsetPreview === true) { orphans.push(obj); return; }
      // 2) Snap marker (createSnapMarker → name '__snapMarker' veya userData.isSnapMarker)
      if (ud.isSnapMarker === true) { orphans.push(obj); return; }
      if (obj.name === '__snapMarker') { orphans.push(obj); return; }
      // 3) Temp wire preview'lar (eski isim-bazlı yol — geriye uyumluluk)
      if (obj.name && (
        (obj.name.includes('temp') && obj.name.includes('WirePreview')) ||
        obj.name === 'tempBellWirePreview' ||
        obj.name === 'tempSocketWirePreview' ||
        obj.name === 'tempmachineWirePreview' ||
        obj.name === 'templightingWirePreview' ||
        obj.name === 'tempfixtureWirePreview'
      )) {
        orphans.push(obj);
      }
    });
    let removed = 0;
    orphans.forEach(obj => {
      try {
        if (obj.parent) obj.parent.remove(obj);
        else scene.remove(obj);
        if (obj.geometry?.dispose) obj.geometry.dispose();
        if (obj.material?.dispose) obj.material.dispose();
        // Group ise children'ı da dispose et
        if (Array.isArray(obj.children)) {
          obj.children.forEach(c => {
            if (c.geometry?.dispose) c.geometry.dispose();
            if (c.material?.dispose) c.material.dispose();
          });
        }
        removed++;
      } catch (e) {
        console.warn('Failed to cleanup orphan guide', e);
      }
    });
    return removed;
  }

  /**
   * Wire çizimi için tüm geçici nesneleri temizler
   * @param {Object} refs - Ref objelerini içeren obje
   * @param {THREE.Scene} scene - Three.js scene
   *
   * refs objesi şu ref'leri içerebilir:
   * - tempLineRef
   * - snapMarkerRef
   * - previewSymbolRef
   * - previewMarkersRef (array)
   * - wireMarkersRef (array)
   * - offsetLineRef
   * - intersectedLineRef
   * - verticesRef (array - sadece temizlenir)
   * - startRef (boolean - false yapılır)
   * - snapSourceRef (null yapılır)
   */
  static cleanupWiringSession(refs, scene) {
    const {
      tempLineRef,
      snapMarkerRef,
      previewSymbolRef,
      previewMarkersRef,
      wireMarkersRef,
      offsetLineRef,
      intersectedLineRef,
      verticesRef,
      startRef,
      snapSourceRef,
      machineSymbollRef,
      undoStackRef,
      redoStackRef,
      machinesRef,
      machineCountRef,
    } = refs;

    // Line ve marker temizliği
    this.cleanupRef(tempLineRef, scene);
    this.cleanupRef(snapMarkerRef, scene);
    this.cleanupRef(previewSymbolRef, scene);
    this.cleanupRef(machineSymbollRef, scene);
    this.cleanupRef(intersectedLineRef, scene);

    // Array temizliği
    this.cleanupRefArray(previewMarkersRef, scene);
    this.cleanupRefArray(wireMarkersRef, scene);

    // Offset lines (özel durum)
    this.cleanupOffsetLines(offsetLineRef, scene);

    // ✅ Ekstra güvenlik: Scene'den orphan preview objelerini temizle
    // Ref'ler temizlendi ama bazı objeler hala scene'de kalabilir (ilk hat senaryosu).
    // userData işaretleri ile isim bazlı tarama birlikte kullanılır:
    //  - userData.isOffsetPreview → magenta/cyan dashed offset rehber çizgileri
    //  - userData.isSnapMarker veya name '__snapMarker' → snap noktası işaretçileri
    //  - name '*WirePreview*' → temp wire preview'ları (eski isim-bazlı yol korundu)
    if (scene) {
      this.removeOrphanGuides(scene);
    }

    // Ref değerlerini sıfırla
    if (verticesRef && verticesRef.current) {
      verticesRef.current = [];
    }

    if (startRef) {
      startRef.current = false;
    }

    if (snapSourceRef) {
      snapSourceRef.current = null;
    }

    if (undoStackRef && undoStackRef.current) {
      undoStackRef.current = [];
    }

    if (redoStackRef && redoStackRef.current) {
      redoStackRef.current = [];
    }

    if (machinesRef && machinesRef.current) {
      machinesRef.current = [];
    }

    if (machineCountRef && machineCountRef.current !== undefined) {
      machineCountRef.current = 0;
    }
  }

  /**
   * ESC tuşu için hızlı temizlik (cancelWiring için)
   * Wiring session'ı iptal eder ve tüm geçici nesneleri temizler
   * @param {Object} refs - Ref objelerini içeren obje
   * @param {THREE.Scene} scene - Three.js scene
   * @param {Function} dispatch - Redux dispatch fonksiyonu
   * @param {Function} resetOperation - Redux resetOperation action
   * @param {Function} setCommandMessage - Redux setCommandMessage action
   * @param {Function} setStep - useState setStep fonksiyonu
   */
  static cancelWiringSession(refs, scene, dispatch, resetOperation, setCommandMessage, setStep) {
    // Önce tüm nesneleri temizle
    this.cleanupWiringSession(refs, scene);

    // Redux state'i sıfırla
    if (dispatch) {
      if (resetOperation) dispatch(resetOperation());
      if (setCommandMessage) dispatch(setCommandMessage(''));
    }

    // Step'i sıfırla
    if (setStep) {
      setStep(0);
    }
  }

  /**
   * Tek bir Three.js objesini dispose eder (standalone kullanım)
   * @param {THREE.Object3D} object - Three.js objesi
   * @param {THREE.Scene} scene - Three.js scene (optional)
   */
  static disposeObject(object, scene = null) {
    if (!object) return;

    if (scene) {
      scene.remove(object);
    }

    if (object.geometry) {
      object.geometry.dispose();
    }

    if (object.material) {
      if (Array.isArray(object.material)) {
        object.material.forEach(mat => {
          if (mat) mat.dispose();
        });
      } else {
        object.material.dispose();
      }
    }

    // Children temizliği
    if (object.children && object.children.length > 0) {
      object.traverse(child => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(mat => {
              if (mat) mat.dispose();
            });
          } else {
            child.material.dispose();
          }
        }
      });
    }
  }
}

export default SceneCleanupManager;
